import { useEffect, useState } from "react"
import { Localized, useLocalization } from "@fluent/react"
import { Alert, Button, Card, CardContent, CardHeader, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { APIError } from "../../../app/appTypes"
import { SaveButton } from "../../../app/components/SaveButton"
import { CancelButton } from "../../../app/components/CancelButton"
import { LpField } from "../../../app/components/LpField"
import { showSuccess } from "../../notifications/notificationsSlice"
import { DOCUMENT_TYPES, DocumentTypes } from "../../documents/documentTypes"
import { DocumentTypesLabel } from "../../documents/components/DocumentTypesLabel"
import { selectSettings } from "../../settings/settingsSlice"
import {
    CompanyDocumentNumberRule,
    SaveCompanyDocumentNumberRule,
    createCompanyDocumentNumberRule,
    loadCompanyDocumentNumberRules,
    selectCompanyDocumentNumberRules,
    setCompanyDocumentNumberRuleActive,
    updateCompanyDocumentNumberRule
} from "./companyDocumentNumberRulesSlice"

interface RuleForm {
    id?: number
    values: SaveCompanyDocumentNumberRule
}

const NUMBER_RULE_DOCUMENT_TYPES: DocumentTypes[] = ["URS", "RA", "DQ"]

const emptyValues: SaveCompanyDocumentNumberRule = {
    documentType: "",
    pattern: "",
    formatDescription: null,
    exampleNumber: null
}

export const CompanyDocumentNumberRulesCard = () => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const { items, status, error: loadingError } = useAppSelector(selectCompanyDocumentNumberRules)
    const [form, setForm] = useState<RuleForm | null>(null)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<APIError | null>(null)
    const [validationError, setValidationError] = useState(false)

    useEffect(() => {
        if (status === "empty") dispatch(loadCompanyDocumentNumberRules())
    }, [dispatch, status])

    const errorMessage = (failure: APIError) =>
        failure.kind === "http" && failure.problem
            ? l10n.getString(failure.problem.title, null, `_${failure.problem.detail || failure.problem.title}`)
            : l10n.getString("company-document-number-rules-error", null, "_Nie udało się wykonać operacji.")

    const updateField = (field: keyof SaveCompanyDocumentNumberRule, value: string) => {
        setForm(current => current && { ...current, values: { ...current.values, [field]: value } })
        setError(null)
        setValidationError(false)
    }

    const editRule = (rule: CompanyDocumentNumberRule) => {
        setForm({
            id: rule.id,
            values: {
                documentType: rule.documentType,
                pattern: rule.pattern,
                formatDescription: rule.formatDescription,
                exampleNumber: rule.exampleNumber
            }
        })
        setError(null)
        setValidationError(false)
    }

    const getSaveValues = (values: SaveCompanyDocumentNumberRule): SaveCompanyDocumentNumberRule => ({
        documentType: values.documentType.trim(),
        pattern: values.pattern.trim(),
        formatDescription: values.formatDescription?.trim() || null,
        exampleNumber: values.exampleNumber?.trim() || null
    })

    const isValid = (values: SaveCompanyDocumentNumberRule, editing: boolean) =>
        (editing || NUMBER_RULE_DOCUMENT_TYPES.some(type => type === values.documentType))
        && !!values.documentType && !!values.pattern && values.documentType.length <= 20 && values.pattern.length <= 2000
        && (values.formatDescription?.length ?? 0) <= 1000
        && (values.exampleNumber?.length ?? 0) <= 256

    const saveRule = async () => {
        if (!form || saving) return
        const dto = getSaveValues(form.values)
        if (!isValid(dto, form.id !== undefined)) {
            setValidationError(true)
            return
        }

        setSaving(true)
        setError(null)
        try {
            const result = form.id === undefined
                ? await dispatch(createCompanyDocumentNumberRule(dto))
                : await dispatch(updateCompanyDocumentNumberRule({ id: form.id, dto }))
            if (createCompanyDocumentNumberRule.fulfilled.match(result) || updateCompanyDocumentNumberRule.fulfilled.match(result)) {
                setForm(null)
                dispatch(showSuccess("saved"))
            } else {
                setError(result.payload ?? { kind: "unknown" })
            }
        } finally {
            setSaving(false)
        }
    }

    const toggleRule = async (rule: CompanyDocumentNumberRule) => {
        setSaving(true)
        setError(null)
        try {
            const result = await dispatch(setCompanyDocumentNumberRuleActive({ id: rule.id, active: !rule.isActive }))
            if (setCompanyDocumentNumberRuleActive.fulfilled.match(result)) {
                dispatch(showSuccess("saved"))
            } else {
                setError(result.payload ?? { kind: "unknown" })
            }
        } finally {
            setSaving(false)
        }
    }

    const valid = !!form && isValid(getSaveValues(form.values), form.id !== undefined)

    const requestSave = () => {
        if (!form || saving) return
        if (!valid) {
            setValidationError(true)
            return
        }
        void saveRule()
    }

    const closeForm = () => {
        setForm(null)
        setError(null)
        setValidationError(false)
    }

    const documentTypeLabel = (code: string) => {
        const type = DOCUMENT_TYPES.find(item => item === code)
        return type ? <DocumentTypesLabel docType={type} lang={locale === "pl" ? "pl" : "en"} /> : code
    }

    const formFields = form && (
        <>
            <TextField select required fullWidth disabled={saving || form.id !== undefined}
                label={l10n.getString("company-document-number-rules-document-type", null, "_Typ dokumentu")}
                value={NUMBER_RULE_DOCUMENT_TYPES.some(type => type === form.values.documentType) || form.id !== undefined
                    ? form.values.documentType : ""}
                onChange={e => updateField("documentType", e.target.value)}>
                {form.id !== undefined && !NUMBER_RULE_DOCUMENT_TYPES.some(type => type === form.values.documentType) &&
                    <MenuItem value={form.values.documentType}>{documentTypeLabel(form.values.documentType)}</MenuItem>}
                {NUMBER_RULE_DOCUMENT_TYPES.map(type => (
                    <MenuItem key={type} value={type}>
                        <DocumentTypesLabel docType={type} lang={locale === "pl" ? "pl" : "en"} />
                    </MenuItem>
                ))}
            </TextField>
            <TextField required fullWidth multiline minRows={2} disabled={saving} inputProps={{ maxLength: 2000 }}
                label={l10n.getString("company-document-number-rules-pattern", null, "_Wzorzec")}
                value={form.values.pattern} onChange={e => updateField("pattern", e.target.value)} />
            <TextField fullWidth multiline minRows={2} disabled={saving} inputProps={{ maxLength: 1000 }}
                label={l10n.getString("company-document-number-rules-description", null, "_Opis formatu")}
                value={form.values.formatDescription ?? ""} onChange={e => updateField("formatDescription", e.target.value)} />
            <TextField fullWidth disabled={saving} inputProps={{ maxLength: 256 }}
                label={l10n.getString("company-document-number-rules-example-number", null, "_Przykładowy numer")}
                value={form.values.exampleNumber ?? ""} onChange={e => updateField("exampleNumber", e.target.value)} />
            {validationError && <Alert severity="error">
                <Localized id="company-document-number-rules-validation">_Sprawdź wymagane pola i ich długość.</Localized>
            </Alert>}
            {error && <Alert severity="error" onClose={() => setError(null)}>{errorMessage(error)}</Alert>}
        </>
    )

    return (
        <Card sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-document-number-rules"><span>_Reguły numeracji dokumentów</span></Localized>}
                sx={{ textAlign: "center", bgcolor: "primary.main", color: "white", py: 1, "& .MuiCardHeader-title": { fontSize: "1rem" } }}
            />
            <CardContent>
                {status === "loading" && <Typography><Localized id="company-document-number-rules-loading">_Ładowanie reguł...</Localized></Typography>}
                {status === "error" && (
                    <Stack spacing={1}>
                        <Alert severity="error">{errorMessage(loadingError ?? { kind: "unknown" })}</Alert>
                        <Button onClick={() => dispatch(loadCompanyDocumentNumberRules())}>
                            <Localized id="company-document-number-rules-retry">_Spróbuj ponownie</Localized>
                        </Button>
                    </Stack>
                )}
                {status === "loaded" && (
                    <Stack spacing={2}>
                        {items.length === 0 && <Typography><Localized id="company-document-number-rules-empty">_Brak reguł numeracji.</Localized></Typography>}
                        <TableContainer>
                            <Table size="small" sx={{ minWidth: 1050, tableLayout: "fixed" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 64 }}><Localized id="company-document-number-rules-ordinal">_Lp.</Localized></TableCell>
                                        <TableCell><Localized id="company-document-number-rules-document-type">_Typ dokumentu</Localized></TableCell>
                                        <TableCell><Localized id="company-document-number-rules-pattern">_Wzorzec</Localized></TableCell>
                                        <TableCell><Localized id="company-document-number-rules-description">_Opis formatu</Localized></TableCell>
                                        <TableCell><Localized id="company-document-number-rules-example-number">_Przykładowy numer</Localized></TableCell>
                                        <TableCell sx={{ width: 140 }}><Localized id="company-document-number-rules-status">_Status</Localized></TableCell>
                                        <TableCell sx={{ width: 245 }}><Localized id="actions">_Akcje</Localized></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.map((rule, index) => (
                                        <TableRow key={rule.id}>
                                            <TableCell><LpField value={index + 1} /></TableCell>
                                            <TableCell>{documentTypeLabel(rule.documentType)}</TableCell>
                                            <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 260 }}>{rule.pattern}</TableCell>
                                            <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 220 }}>{rule.formatDescription}</TableCell>
                                            <TableCell sx={{ overflowWrap: "anywhere", maxWidth: 180 }}>{rule.exampleNumber}</TableCell>
                                            <TableCell>
                                                <Chip size="small" color={rule.isActive ? "success" : "default"}
                                                    label={rule.isActive
                                                        ? l10n.getString("company-document-number-rules-active", null, "_Aktywna")
                                                        : l10n.getString("company-document-number-rules-inactive", null, "_Nieaktywna")} />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    <Button variant="contained" color="primary" size="small" disabled={saving} onClick={() => editRule(rule)}>
                                                        <Localized id="edit">_Edytuj</Localized>
                                                    </Button>
                                                    <Button variant="contained" color={rule.isActive ? "warning" : "success"} size="small"
                                                        sx={{ width: 125 }} disabled={saving} onClick={() => toggleRule(rule)}>
                                                        {rule.isActive
                                                            ? <Localized id="company-document-number-rules-deactivate">_Dezaktywuj</Localized>
                                                            : <Localized id="company-document-number-rules-activate">_Aktywuj</Localized>}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {!form && <Button variant="contained" color="primary" sx={{ alignSelf: "flex-start" }}
                            onClick={() => { setForm({ values: { ...emptyValues } }); setError(null); setValidationError(false) }}>
                            <Localized id="company-document-number-rules-add">_Dodaj regułę</Localized>
                        </Button>}
                    </Stack>
                )}
                {error && !form && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{errorMessage(error)}</Alert>}
            </CardContent>
            {form && <Dialog open={true} fullWidth maxWidth="sm">
                <DialogTitle sx={{ bgcolor: "grey.200", fontWeight: "bold" }}>
                    {form?.id === undefined
                        ? <Localized id="company-document-number-rules-add">_Dodaj regułę</Localized>
                        : <Localized id="company-document-number-rules-edit">_Edytuj regułę</Localized>}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>{formFields}</Stack>
                </DialogContent>
                <DialogActions>
                    <SaveButton loading={saving} disabled={!valid} onClick={requestSave} />
                    <CancelButton loading={saving} onClick={closeForm} />
                </DialogActions>
            </Dialog>
            }
        </Card>
    )
}
